import { observable, raw } from '@nx-js/observer-util';
import type ApplicationModel from 'components/framework/ApplicationModel';
import { compareWithFallbacks } from 'components/framework/lib/compareUtils';
import { modelInstance } from 'components/framework/modelBuilder';
import { keyBy, uniq } from 'lodash';
import Fragment from '../../Fragment';
import BookmarksRecordPosition from '../BookmarksRecordPosition';
import {
  type PrioritizableRecordUnion,
  type RecordPlacementEnum_value,
  type RecordUnion,
} from '../generated/types';
import type ItemPlacement from '../ItemPlacement';

type SecondaryScopeGetter =
  | ((record: ApplicationModel) => RecordId | undefined)
  | undefined;

export const placeRecords = async ({
  bookmarkType,
  bookmarkId,
  secondaryScopeId = bookmarkId,
  records,
  placement,
}: {
  bookmarkType: string;
  bookmarkId: RecordId;
  secondaryScopeId?: RecordId;
  records: (RecordUnion | PrioritizableRecordUnion)[];
  placement: ItemPlacement;
}) => {
  if (records.length === 0) {
    return;
  }

  const recordPositionScope =
    records[0]?.query?.subqueries?.bookmarksRecordPositions;
  const scopeAttributes = recordPositionScope?.attrs || [
    'position',
    'bookmarkType',
    'secondaryScope',
  ];

  const recordReferences = records.map(({ typename, id }) => ({
    typename,
    id,
  }));

  let fragment = new Fragment('placeRecordsInPosition').argument(
    'attributes',
    {
      bookmarkType,
      bookmarkId,
      secondaryScopeId,
      records: recordReferences,
      placement: placement.toRecordPlacementInput(),
    },
    'PlaceRecordsInPositionAttributes!'
  );

  fragment = fragment.fragment(
    new Fragment('bookmarksRecordPositions').attr(
      ...uniq(['recordType', 'recordId', ...scopeAttributes])
    )
  );

  const result = await BookmarksRecordPosition.mutate(fragment);

  const newPositions =
    // @ts-ignore
    result.placeRecordsInPosition?.bookmarksRecordPositions || [];
  const groupedPositions: Record<string, BookmarksRecordPosition> = keyBy(
    newPositions,
    position => `${position.recordType}-${position.recordId}`
  );

  records.forEach(record => {
    const recordPosition =
      groupedPositions[`${record.internalTypename}-${record.id}`];
    const { bookmarksRecordPositions = [] } = record;
    const index = bookmarksRecordPositions.findIndex(
      position =>
        position.bookmarkType === bookmarkType &&
        position.secondaryScope === secondaryScopeId
    );
    const existingPosition = bookmarksRecordPositions[index];

    if (!recordPosition) {
      console.error('Could not find record position that was just created');
      return;
    }

    if (existingPosition) {
      if (typeof existingPosition?.resetAttributes === 'function') {
        existingPosition.resetAttributes(recordPosition);
      } else {
        bookmarksRecordPositions.splice(index, 1, recordPosition);
      }
    } else {
      bookmarksRecordPositions.push(
        observable(modelInstance<BookmarksRecordPosition>(recordPosition))
      );
    }
  });
};

interface SetRecordPositionProps {
  bookmarkType: string;
  bookmarkId: RecordId;
  prev?: ApplicationModel;
  next?: ApplicationModel;
  secondaryScopeId?: RecordId;
  placement?: RecordPlacementEnum_value;
  rank?: number;
  options?: { args?: object };
}

type UnsetRecordPositionProps = {
  bookmarkType: string;
  bookmarkId: RecordId;
} & ({ secondaryScopeId: RecordId | null } | { all: true });

export interface HasRecordPositions {
  recordPosition(type: string, secondaryScopeGetter?: SecondaryScopeGetter);
  compareRecordPosition(
    type: string,
    other: any,
    secondaryScopeGetter: SecondaryScopeGetter
  ): ReturnType<typeof compareWithFallbacks>;
  hasRecordPosition(
    type: string,
    secondaryScopeGetter?: SecondaryScopeGetter
  ): boolean;
  setRecordPosition(props: SetRecordPositionProps): Promise<void>;
  addRecordPositionLocal(
    props: Pick<
      SetRecordPositionProps,
      'bookmarkId' | 'secondaryScopeId' | 'placement'
    >
  ): void;
  removeRecordPosition(props: UnsetRecordPositionProps): Promise<void>;
}

export function HasRecordPositions(originalClass: {
  new (...args: any[]): ApplicationModel & {
    position?: number;
    id: RecordId;
    bookmarksRecordPositions?: BookmarksRecordPosition[];
  };
}): any {
  return class extends originalClass implements HasRecordPositions {
    compareRecordPosition(
      type,
      other,
      secondaryScopeGetter: SecondaryScopeGetter
    ) {
      return compareWithFallbacks(
        [
          this.recordPosition(type, secondaryScopeGetter)?.position,
          this.position,
          this.id,
        ],
        [
          other.recordPosition(type, secondaryScopeGetter)?.position,
          other.position,
          other.id,
        ]
      );
    }

    recordPosition(type: string, secondaryScopeGetter?: SecondaryScopeGetter) {
      if (this.bookmarksRecordPositions) {
        return this.bookmarksRecordPositions.find(
          pos =>
            pos.bookmarkType === type &&
            (typeof secondaryScopeGetter !== 'function' ||
              pos.secondaryScope === secondaryScopeGetter(this))
        );
      }
    }

    hasRecordPosition(type, secondaryScopeGetter?: SecondaryScopeGetter) {
      return !!this.recordPosition(type, secondaryScopeGetter);
    }

    async setRecordPosition({
      bookmarkType,
      bookmarkId,
      prev,
      next,
      secondaryScopeId = bookmarkId,
      placement = undefined,
      rank = undefined,
      options = {},
    }: {
      bookmarkType: string;
      bookmarkId: RecordId;
      prev?: ApplicationModel;
      next?: ApplicationModel;
      secondaryScopeId?: RecordId;
      placement?: string;
      rank?: number;
      options?: { args?: object };
    }) {
      let fragment = new Fragment('setBookmarksRecordPosition').argument(
        'attributes',
        {
          bookmarkType,
          bookmarkId,
          secondaryScopeId,
          recordType: this.typename,
          recordId: this.id,
          // @ts-ignore
          placeAfterType: prev?.constructor?.typename || null,
          // @ts-ignore
          placeAfterId: prev?.id || null,
          // @ts-ignore
          placeBeforeType: next?.constructor?.typename || null,
          // @ts-ignore
          placeBeforeId: next?.id || null,
          placement,
          rank,
        },
        'RecordPositionAttributes!'
      );

      if (options.args) {
        Object.entries(options.args).forEach(([key, value]) => {
          fragment = fragment.argument(key, value, undefined);
        });
      }

      fragment = fragment.fragment(
        new Fragment('bookmarksRecordPosition').attr(
          'position',
          'bookmarkType',
          'secondaryScope'
        )
      );

      const result = await BookmarksRecordPosition.mutate<{
        setBookmarksRecordPosition: {
          bookmarksRecordPosition: {
            position: number;
            bookmarkType: string;
            secondaryScope: string;
          };
        };
      }>(fragment);

      const newPosition =
        result.setBookmarksRecordPosition.bookmarksRecordPosition;

      if (this.bookmarksRecordPositions && newPosition) {
        // add position for the bookmarkType, set value directly to avoid flagging as dirty
        const filteredPositions = observable(
          raw(this.bookmarksRecordPositions).filter(
            bookmarksRecordPosition =>
              bookmarksRecordPosition.bookmarkType !== bookmarkType &&
              bookmarksRecordPosition.secondaryScope !==
                newPosition.secondaryScope
          )
        );
        // @ts-ignore we're pushing on a plain object instead of a model instance
        filteredPositions.push(newPosition);
        this.setAttribute('bookmarksRecordPositions', filteredPositions, false);
      }
    }

    addRecordPositionLocal(props: SetRecordPositionProps): void {
      if (!this.bookmarksRecordPositions) {
        this.bookmarksRecordPositions = [];
      }

      const position = new BookmarksRecordPosition();
      position.bookmarkId = props.bookmarkId;
      if (props.secondaryScopeId) {
        position.secondaryScopeId = props.secondaryScopeId;
      }
      position.placement = props.placement ?? null;
      this.bookmarksRecordPositions.push(position);
    }

    async removeRecordPosition(props: UnsetRecordPositionProps) {
      if (this.bookmarksRecordPositions) {
        // filter out positions for the bookmarkType, set value directly to avoid flagging as dirty
        const filteredPositions = observable(
          raw(this.bookmarksRecordPositions).filter(
            bookmarksRecordPosition =>
              bookmarksRecordPosition.bookmarkType !== props.bookmarkType
          )
        );
        this.setAttribute('bookmarksRecordPositions', filteredPositions, false);
      }

      // Delete the record position with a static mutation, deleting the position using only the bookmark and record
      const fragment = new Fragment('unsetBookmarksRecordPosition').argument(
        'attributes',
        {
          ...props,
          recordType: this.typename,
          recordId: this.id,
        },
        'UnsetRecordPositionAttributes!'
      );

      await BookmarksRecordPosition.mutate(fragment);
    }
  };
}
